import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminBilling() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [failedPayments, setFailedPayments] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("subscriptions");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");

  useEffect(() => {
    fetchBillingData();
  }, [page, statusFilter, planFilter, activeTab]);

  const fetchBillingData = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      if (activeTab === "subscriptions") {
        const params = new URLSearchParams({ page, limit: 20 });
        if (statusFilter) params.append("status", statusFilter);
        if (planFilter) params.append("plan", planFilter);

        const [subsRes, statsRes] = await Promise.all([
          axios.get(`${API}/admin/billing/subscriptions?${params}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${API}/admin/billing/stats`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setSubscriptions(subsRes.data.subscriptions);
        setTotalPages(subsRes.data.total_pages);
        setStats(statsRes.data);
      } else if (activeTab === "webhooks") {
        const response = await axios.get(`${API}/admin/billing/webhooks?page=${page}&limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setWebhooks(response.data.events);
        setTotalPages(response.data.total_pages);
      } else if (activeTab === "failed") {
        const response = await axios.get(`${API}/admin/billing/failed-payments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFailedPayments(response.data);
      }
    } catch (error) {
      toast.error("Failed to load billing data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: "bg-green-600",
      trial: "bg-yellow-600",
      cancelled: "bg-red-600",
      past_due: "bg-orange-600",
    };
    return colors[status] || "bg-slate-600";
  };

  const getPlanBadge = (plan) => {
    const colors = {
      free: "bg-slate-600",
      starter: "bg-blue-600",
      pro: "bg-purple-600",
      org: "bg-teal-600",
    };
    return colors[plan] || "bg-slate-600";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Billing</h1>
        <p className="text-muted-foreground mt-1">Stripe subscriptions, webhooks, and payments</p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Active Subscriptions</p>
            <p className="text-3xl font-bold text-foreground">{stats.subscription_status?.active || 0}</p>
          </div>
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Past Due</p>
            <p className="text-3xl font-bold text-orange-400">{stats.subscription_status?.past_due || 0}</p>
          </div>
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Webhooks (7d)</p>
            <p className="text-3xl font-bold text-foreground">{stats.webhooks_7d || 0}</p>
          </div>
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
            <p className="text-muted-foreground text-sm mb-2">Failed Webhooks</p>
            <p className="text-3xl font-bold text-red-400">{stats.failed_webhooks_7d || 0}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-2 flex gap-2">
        <button
          onClick={() => {
            setActiveTab("subscriptions");
            setPage(1);
          }}
          className={`flex-1 px-4 py-2 rounded-lg transition ${
            activeTab === "subscriptions"
              ? "bg-primary text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          Subscriptions
        </button>
        <button
          onClick={() => {
            setActiveTab("webhooks");
            setPage(1);
          }}
          className={`flex-1 px-4 py-2 rounded-lg transition ${
            activeTab === "webhooks"
              ? "bg-primary text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          Webhook Events
        </button>
        <button
          onClick={() => {
            setActiveTab("failed");
            setPage(1);
          }}
          className={`flex-1 px-4 py-2 rounded-lg transition ${
            activeTab === "failed"
              ? "bg-primary text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted"
          }`}
        >
          Failed Payments
        </button>
      </div>

      {/* Subscriptions Tab */}
      {activeTab === "subscriptions" && (
        <>
          {/* Filters */}
          <div className="flex gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="trial">Trial</option>
              <option value="cancelled">Cancelled</option>
              <option value="past_due">Past Due</option>
            </select>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Plans</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="org">Organization</option>
            </select>
          </div>

          {/* Subscriptions Table */}
          <div className="bg-card backdrop-blur-sm border border-border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No subscriptions found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">User</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Plan</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Cycle</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Stripe ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {subscriptions.map((sub) => (
                      <tr key={sub.id} className="hover:bg-muted/30 transition">
                        <td className="px-6 py-4">
                          <p className="text-foreground font-medium">{sub.user_name}</p>
                          <p className="text-muted-foreground text-sm">{sub.user_email}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 ${getPlanBadge(sub.plan_type)} text-foreground text-xs rounded-full capitalize`}>
                            {sub.plan_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-foreground capitalize">{sub.billing_cycle || "-"}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 ${getStatusBadge(sub.status)} text-foreground text-xs rounded-full capitalize`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-muted-foreground text-sm">
                          {new Date(sub.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          {sub.stripe_subscription_id ? (
                            <span className="text-xs text-muted-foreground font-mono">
                              {sub.stripe_subscription_id.substring(0, 12)}...
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Webhooks Tab */}
      {activeTab === "webhooks" && (
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No webhook events found</p>
              <p className="text-muted-foreground text-sm mt-2">Webhook events will appear here when Stripe sends them</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Event ID</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {webhooks.map((event, idx) => (
                    <tr key={idx} className="hover:bg-muted/30 transition">
                      <td className="px-6 py-4 text-foreground font-mono text-sm">{event.event_type}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 ${
                            event.status === "success" ? "bg-green-600" : "bg-red-600"
                          } text-foreground text-xs rounded-full`}
                        >
                          {event.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground text-sm">
                        {new Date(event.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-xs text-muted-foreground font-mono">{event.event_id?.substring(0, 16)}...</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Failed Payments Tab */}
      {activeTab === "failed" && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : (
            <>
              {/* Failed Payments Queue */}
              <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-foreground">Past Due Users</h2>
                  <span className="px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-sm">
                    {failedPayments?.total_past_due || 0} users
                  </span>
                </div>

                {failedPayments?.past_due_users?.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <p className="text-muted-foreground">No failed payments - all clear!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {failedPayments?.past_due_users?.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-4 bg-red-500/5 border border-red-500/20 rounded-lg"
                      >
                        <div>
                          <p className="text-foreground font-medium">{user.name}</p>
                          <p className="text-muted-foreground text-sm">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 ${getPlanBadge(user.plan_type)} text-foreground text-xs rounded-full capitalize`}>
                            {user.plan_type}
                          </span>
                          <button className="px-3 py-1 bg-orange-600 hover:bg-orange-700 text-foreground text-sm rounded transition">
                            Contact
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Recent Failures */}
              <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
                <h2 className="text-xl font-bold text-foreground mb-6">Recent Payment Failures (7 days)</h2>
                {failedPayments?.recent_failures?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">No recent failures</p>
                ) : (
                  <div className="space-y-2">
                    {failedPayments?.recent_failures?.map((event, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-foreground font-mono text-sm">{event.event_type}</p>
                          <p className="text-muted-foreground text-xs">
                            {new Date(event.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && activeTab !== "failed" && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="px-4 py-2 bg-muted hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-muted-foreground">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 bg-muted hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed text-foreground rounded-lg transition"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
